import React from 'react';
import SimpleStepContent from '../components/helpTour/components/SimpleStepContent';
import AppConfig from '../appConfig';
import LocationAnalysisItemOrigin from '../enums/LocationAnalysisItemOrigin';
import BaseStepContent from '../components/helpTour/components/BaseStepContent';
import InfoPanelStep from '../components/helpTour/components/InfoPanelStep';

export const HelpTourVersions = {
    V1: 'v1',
    V2: 'v2',
};

export const HelpTourTargets = {
    FIND_OPPORTUNITIES: 'tour-target-find-opportunities',
    MAP_LAYERS_PANEL: 'tour-target-map-layers-panel',
    INFO_PANEL: 'tour-target-info-panel',
    INFO_PANEL_RADIUS_SELECTOR: 'tour-target-info-panel-radius-selector',
    INFO_PANEL_EXPORT_DATA: 'tour-target-info-panel-export-data',
    INFO_PANEL_FULL_SCREEN: 'tour-target-info-panel-full-screen',
};

export const HelpTourWelcomeOptions = {
    targetId: HelpTourTargets.WELCOME,
    highlightId: HelpTourTargets.WELCOME,
    position: {
        anchorOrigin: { horizontal: 'middle', vertical: 'center' },
        targetOrigin: { horizontal: 'middle', vertical: 'center' },
    },
};

export const DEFAULT_ARROW_OFFSET = 32;
const DEFAULT_MARGIN_OFFSET = 30;
const DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET = -420;

/** @type {import('../types').HelpTourStep} */
const findOpportunitiesStep = {
    id: 'find_opportunities',
    targetId: HelpTourTargets.FIND_OPPORTUNITIES,
    highlightId: HelpTourTargets.FIND_OPPORTUNITIES,
    title: 'Find New Opportunities',
    content: (
        <BaseStepContent>
            <div className="step-content">
                <p>One location for all your sleuthing needs!</p>
                <ol>
                    <li>
                        <strong>Filter areas by criteria</strong> allows you to filter by your trade
                        area buybox.
                    </li>
                    <li>
                        <strong>Find facilities</strong>: hide the facilities that you aren't
                        interested in so you can focus your attention on the most promising.
                    </li>
                </ol>
                <p>
                    You can use these two features together to identify a shortlist of
                    opportunities. For example, if you want to find facilities less than 40,000 NRSF
                    in markets with low supply per capita, high median income, and high population
                    growth.
                </p>
            </div>
        </BaseStepContent>
    ),
    classes: 'arrow-pointer arrow-west arrow-top stretched',
    position: {
        marginLeft: 20,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        targetOrigin: { horizontal: 'left', vertical: 'top' },
    },
    verticalCentering: true,
    nextStepId: 'map_layers',
};

/** @type {Record<import('../types').HelpTourStepId, import('../types').HelpTourStep>} */
export const HelpTourNewUserSteps = {
    find_opportunities: findOpportunitiesStep,
    map_layers: {
        id: 'map_layers',
        targetId: HelpTourTargets.MAP_LAYERS_PANEL,
        highlightId: HelpTourTargets.MAP_LAYERS_PANEL,
        title: 'Analyze with critical layers',
        content: (
            <SimpleStepContent content="Screening and underwriting a deal requires looking at things from many different views. TractIQ Layers allows you to change the background and overlay additional data so you can fully understand the market." />
        ),
        classes: 'arrow-pointer arrow-west arrow-top',
        position: {
            marginLeft: DEFAULT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'find_opportunities',
        nextStepId: 'radius_selection',
    },
    radius_selection: {
        id: 'radius_selection',
        targetId: HelpTourTargets.INFO_PANEL_RADIUS_SELECTOR,
        highlightId: HelpTourTargets.INFO_PANEL_RADIUS_SELECTOR,
        title: 'Change radius',
        content: (
            <InfoPanelStep
                stepId="radius_selection"
                content="Analyze any trade area up to 20 mile radius or 60 minute drive time."
            />
        ),
        classes: 'arrow-pointer arrow-east arrow-top',
        position: {
            marginTop: -15,
            marginLeft: DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'map_layers',
        nextStepId: 'export_data',
        waitForLoading: true,
    },
    export_data: {
        id: 'export_data',
        targetId: HelpTourTargets.INFO_PANEL_EXPORT_DATA,
        highlightId: HelpTourTargets.INFO_PANEL_EXPORT_DATA,
        title: 'Export data',
        content: (
            <InfoPanelStep
                stepId="export_data"
                content="We respect your process and tools. That's why TractIQ data is easily exportable. Here you can access trade area reports like Storage Facilities Report, Demographics Report, Commercial and Residential Development Reports, and Opportunity (saturation) Report."
            />
        ),
        classes: 'arrow-pointer arrow-east arrow-top',
        position: {
            marginTop: -15,
            marginLeft: DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'radius_selection',
        nextStepId: 'info_panel',
        waitForLoading: true,
    },
    info_panel: {
        id: 'info_panel',
        targetId: HelpTourTargets.INFO_PANEL,
        highlightId: HelpTourTargets.INFO_PANEL,
        title: 'Insight Panel',
        content: (
            <InfoPanelStep
                stepId="info_panel"
                content="Whether you want a quick executive summary, historical rental rate trends or you’re doing deep rental rate analysis for specific facilities, the Insight Panel allows you to dive into the market."
            />
        ),
        classes: 'arrow-pointer arrow-east arrow-top',
        position: {
            marginTop: 150,
            marginLeft: DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'export_data',
        nextStepId: 'full_screen_insights',
    },
    full_screen_insights: {
        id: 'full_screen_insights',
        targetId: HelpTourTargets.INFO_PANEL_FULL_SCREEN,
        highlightId: HelpTourTargets.INFO_PANEL_FULL_SCREEN,
        title: 'Deeper dive insights',
        content: (
            <InfoPanelStep
                stepId="full_screen_insights"
                content="Go into full-screen Insight Panel to see trade area comparisons and slice data (like rental comps) more to build the specific breakouts you need."
            />
        ),
        classes: 'arrow-pointer arrow-east arrow-bottom',
        position: {
            marginTop: 50,
            marginLeft: DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'info_panel',
        waitForLoading: true,
    },
};

/** @type {Record<import('../types').HelpTourStepId, import('../types').HelpTourStep} */
export const HelpTourExistingUserSteps = {
    find_opportunities: findOpportunitiesStep,
    map_layers: {
        id: 'map_layers',
        targetId: HelpTourTargets.MAP_LAYERS_PANEL,
        highlightId: HelpTourTargets.MAP_LAYERS_PANEL,
        title: 'Consolidated Layer Libraries',
        content: (
            <SimpleStepContent content="We've updated the default layer libraries. You can easily collapse them out of the way if needed." />
        ),
        classes: 'arrow-pointer arrow-west arrow-top',
        position: {
            marginLeft: DEFAULT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'find_opportunities',
        nextStepId: 'radius_selection',
    },
    radius_selection: {
        id: 'radius_selection',
        targetId: HelpTourTargets.INFO_PANEL_RADIUS_SELECTOR,
        highlightId: HelpTourTargets.INFO_PANEL_RADIUS_SELECTOR,
        title: 'Radius and Reports',
        content: (
            <InfoPanelStep
                stepId="radius_selection"
                content="Update radius and reports – Storage Facilities, Demographic, Opportunity (saturation), Housing and Commercial Developments – have moved to the Insight Panel."
            />
        ),
        classes: 'arrow-pointer arrow-east arrow-top',
        position: {
            marginTop: -15,
            marginLeft: DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'map_layers',
        nextStepId: 'rental_comps',
        waitForLoading: true,
    },
    rental_comps: {
        id: 'rental_comps',
        targetId: HelpTourTargets.INFO_PANEL,
        highlightId: HelpTourTargets.INFO_PANEL,
        title: 'Customize rental comps',
        content: (
            <InfoPanelStep
                stepId="rental_comps"
                content="Analyze rental comps to look at the exact rate slicing you most-need. For example, break out first floor vs upper level for 10x10 CC units to understand ground floor premiums in a market."
                report="selfstorage-comps"
            />
        ),
        classes: 'arrow-pointer arrow-east',
        position: {
            marginTop: 420,
            marginLeft: DEFAULT_INFO_PANEL_LEFT_MARGIN_OFFSET,
            anchorOrigin: { horizontal: 'left', vertical: 'top' },
            targetOrigin: { horizontal: 'left', vertical: 'top' },
        },
        previousStepId: 'radius_selection',
        waitForLoading: true,
    },
};

export const HELP_TOUR_DEFAULT_FACILITY = {
    type: 'ss-facility',
    id: '017581',
    value: 'Cubesmart Self Storage',
    point: { lng: -97.72492289543152, lat: 30.23132687479091 },
    itemOrigin: LocationAnalysisItemOrigin.LIBRARY_LAYER_FEATURE,
    analysisTypeId: AppConfig.constants.defaultLocationAnalysisSelectionType,
    selection: new Set(AppConfig.constants.defaultLocationAnalysisSelection),
};
